import UriJs from 'urijs';
import axios, {CancelToken} from 'axios';
import getAxiosInstance, {BASE_URL_TYPE} from '../api/axios';

const axiosInstance = getAxiosInstance(BASE_URL_TYPE.API);

export const getApiLayers = async (landUseParam, placeID, type = 'land_use_type', options = {}) => {
  // Set options default values.
  options = {
    action: '_land_use',
    type: '_land_use',
    layer_type: 'land_use',
    ...options,
  };
  let url = new UriJs(`${BASE_URL_TYPE.API}/api/ui-api`)
    .search('?rows=200')
    .addSearch('order_by', 'title')
    .addSearch('fields[]', 'id')
    .addSearch('fields[]', 'title')
    .addSearch('fields[]', 'color')
    .addSearch('fields[]', 'jurisdiction')
    .addSearch('action', options.action)
    .addSearch('type', options.type)
    .addSearch('publicToken', 'token')
    .addSearch('state_env', 'fl');

  if (options?.groupByField) {
    url = url.addSearch('fields[]', options?.groupByField);
  }

  if (options.action !== '_land_use') {
    url = url.addSearch('featureLayer', 'state');
    url = url.addSearch('place', placeID);
  }
  else {
    url = url.addSearch(
      'jurisdiction',
      [...Array(10).keys()]
        .map(num => (num ? `${placeID}-${num}` : placeID))
        .concat(options?.parentGridicsId || [])
        .join(','),
    );
    url = url.addSearch('publicVisibility', '1');
  }

  if (type !== 'land_use') {
    url = url.addSearch(type, landUseParam);
  }
  else if (type === 'land_use' && typeof landUseParam === 'object' && landUseParam.length > 0) {
    url = url.addSearch(type, landUseParam.join(','));
  }
  const getFullData = async (urlBuilder, loaded) => {
    const resultUrl = urlBuilder
      .addSearch('offset', loaded.length)
      .normalizeSearch()
      .readable();
    return axiosInstance
      .get(resultUrl)
      .then(({data}) => {
        const {rows} = data;
        const newLoaded = [...loaded, ...data.items];
        if (newLoaded.length < +rows) {
          return getFullData(urlBuilder, newLoaded);
        }

        return newLoaded;
      })
      .catch(() => []);
  };

  return getFullData(url, []).then((layersData) => {
    if (!options.layer_type) {
      return layersData;
    }

    return layersData.map((layerData) => {
      const color = layerData.color || null;
      const layer = {
        layer_title: typeof layerData.title === 'object' ? layerData.title.join(',') : layerData.title,
        jurisdiction: layerData.jurisdiction || [],
        layer_id: layerData.id.toString(),
        layer_type: options.layer_type,
        show_label: '1',
      };
      if (type === 'land_use_type') {
        layer.land_use_type = landUseParam;
      }
      if (color) {
        layer.custom_color = 1;
        layer.color_details = {
          fill_color: color,
          fill_opacity: '0.5',
          border_color: options?.color_details?.border_color || '#000000',
          border_opacity: options?.color_details?.border_opacity || '0.5',
          border_width: options?.color_details?.border_width || '1',
        };
        layer.border_style = options?.border_style || 'solid';
      }
      if (options?.groupByField) {
        layer[options?.groupByField] = layerData?.[options?.groupByField];
      }

      return layer;
    });
  });
};

export const getLandUseTypesLayers = async (subgroup, placeID, parentGridicsId) => {
  let apiLayers = [];
  const promises = [];
  const options = {};
  if (subgroup?.enable_group_by && subgroup?.group_by_field_name) {
    options.groupByField = subgroup?.group_by_field_name;
  }
  if (parentGridicsId) {
    options.parentGridicsId = parentGridicsId;
  }
  // eslint-disable-next-line no-unused-expressions
  subgroup?.land_use_types?.forEach((type) => {
    promises.push(
      getApiLayers(type, placeID, 'land_use_type', options).then((newApiLayers) => {
        apiLayers = [...apiLayers, ...newApiLayers];
        return true;
      }),
    );
  });

  return Promise.all(promises).then(() => apiLayers);
};

export const getLinesCategoriesLayers = async (subgroup, placeID) => {
  let apiLayers = [];
  const promises = [];
  // eslint-disable-next-line no-unused-expressions
  subgroup?.lines_categories?.forEach((type) => {
    promises.push(
      getApiLayers(type, placeID, 'category', {
        action: '_lines',
        type: '_lines',
        layer_type: 'lines',
      }).then((newApiLayers) => {
        apiLayers = [...apiLayers, ...newApiLayers];
        return true;
      }),
    );
  });

  return Promise.all(promises).then(() => apiLayers);
};

export const getPolygonCategoriesLayers = async (subgroup, placeID) => {
  let apiLayers = [];
  const promises = [];
  // eslint-disable-next-line no-unused-expressions
  subgroup?.polygons_categories?.forEach((type) => {
    promises.push(
      getApiLayers(type, placeID, 'category', {
        action: '_polygons',
        type: '_polygons',
        layer_type: 'polygons',
        color_details: subgroup.color_details,
        border_style: subgroup.border_style,
      }).then((newApiLayers) => {
        apiLayers = [...apiLayers, ...newApiLayers];
        return true;
      }),
    );
  });

  return Promise.all(promises).then(() => apiLayers);
};

export const getLandUseCategoriesLayers = async (subgroup, placeID) => {
  let apiLayers = [];
  const promises = [];
  // eslint-disable-next-line no-unused-expressions
  subgroup?.land_use_categories?.forEach((type) => {
    promises.push(
      getApiLayers(type, placeID, 'landUseCategory').then((newApiLayers) => {
        apiLayers = [...apiLayers, ...newApiLayers];
        return true;
      }),
    );
  });

  return Promise.all(promises).then(() => apiLayers);
};

export const fetchSubgroupLayers = async (subgroup, placeID, placeSettings) => {
  if (!placeID) {
    return [];
  }

  const promises = [];
  let totalNewLayers = [];
  if (placeSettings) {
    promises.push(
      getLandUseTypesLayers(subgroup, placeID, placeSettings?.ParentGridicsId).then((newLayers) => {
        totalNewLayers = [...totalNewLayers, ...newLayers];
        return true;
      }),
    );
    promises.push(
      getLandUseCategoriesLayers(subgroup, placeID, placeSettings?.ParentGridicsId).then((newLayers) => {
        totalNewLayers = [...totalNewLayers, ...newLayers];
        return true;
      }),
    );
  }
  promises.push(
    getLinesCategoriesLayers(subgroup, placeID).then((newLayers) => {
      totalNewLayers = [...totalNewLayers, ...newLayers];
      return true;
    }),
  );
  promises.push(
    getPolygonCategoriesLayers(subgroup, placeID).then((newLayers) => {
      totalNewLayers = [...totalNewLayers, ...newLayers];
      return true;
    }),
  );

  return Promise.all(promises).then(() => totalNewLayers);
};

export const mapLayersObject = (layers, callback) => {
  Object.entries(layers).forEach(([name, subgroups]) => {
    Object.entries(subgroups || {}).forEach(([subname, layersArr]) => {
      (layersArr || []).forEach((layer) => {
        callback(name, subname, layer);
      });
    });
  });
};

export const addLayerToPath = (resultObj, name, subname, layer) => {
  if (!resultObj[name]) {
    resultObj[name] = {};
  }

  if (!resultObj[name][subname]) {
    resultObj[name][subname] = [];
  }

  if (!resultObj[name][subname].includes(layer)) {
    resultObj[name][subname].push(layer);
  }
};

export const loadLandUseLayersByIDs = async (ids) => {
  if (!ids || ids.length < 1) {
    return null;
  }
  const rowCount = 200;

  const loadStack = [];
  let stack = [];
  ids.forEach((id) => {
    stack.push(id);

    if (stack.length >= rowCount) {
      loadStack.push([...stack]);
      stack = [];
    }
  });
  if (stack.length) {
    loadStack.push(stack);
  }

  const promises = [];
  let result = [];
  loadStack.forEach((idStack) => {
    const stackUrl = new UriJs(`${BASE_URL_TYPE.API}/api/ui-api`)
      .search(`?rows=${rowCount}`)
      .addSearch('order_by', 'title')
      .addSearch('fields[]', 'id')
      .addSearch('fields[]', 'title')
      .addSearch('type', '_land_use')
      .addSearch('action', '_land_use')
      .addSearch('geometryFormat', 'geojson')
      .addSearch('publicToken', 'token')
      .addSearch('state_env', 'fl')
      .addSearch('id', idStack.join(','))
      .normalizeSearch()
      .readable();

    promises.push(
      axiosInstance.get(stackUrl).then((response) => {
        result = [...result, ...response.data.items];
        return response.data.items;
      }),
    );
  });

  return Promise.all(promises).then(() => result);
};

export const loadGeoJSONLayerByURL = async (url, cancelCallback = () => {}) => {
  if (!url) {
    return null;
  }

  return axiosInstance
    .get(url, {
      cancelToken: new CancelToken((c) => {
        cancelCallback(c);
      }),
    })
    .then(response => response.data?.items || [])
    .catch((thrown) => {
      if (axios.isCancel(thrown)) {
        console.warn('Mapbox GL JS:', thrown.message);
      }
    });
};
